class AlgoliaRefinementList extends HTMLElement {
  constructor() {
    super();

    this.createWidget = this.createWidget.bind(this);
    this.connectedCallback = this.connectedCallback.bind(this);
    this.getSelector = this.getSelector.bind(this);
    this.render = this.render.bind(this);
    this.renderContainer = this.renderContainer.bind(this);
    this.renderContent = this.renderContent.bind(this);
    this.renderWrapper = this.renderWrapper.bind(this);
  }

  /**
   * Initializes settings based on custom attributes when the element is connected to the DOM.
   * @returns {void}
   */
  connectedCallback() {
    this.settings = {
      facetLimitBeforeShowMore: this.getAttribute('facet-limit-before-show-more'),
      showLess: this.getAttribute('show-less'),
      showMore: this.getAttribute('show-more')
    }
  }

  /**
   * Creates an instance of the refinement list widget.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Object} instances - Instances object containing algolia instances.
   * @returns {Object} The created refinement list widget instance.
   */
  createWidget(configuration, instances) {
    const { connectRefinementList } = instances.instantSearch.connectors;
    const connectedRefinementList = connectRefinementList((renderOptions, isFirstRender) => this.render(configuration, isFirstRender, renderOptions));

    const attribute = configuration.name;
    const selector = `[class~='${this.getSelector(configuration)}']`;
    const container = document.querySelector(selector);

    /* Maximum limit set by Algolia. */
    const showMoreLimit = 100;

    /* Widget settings. */
    const refinementListWidget = connectedRefinementList({
      attribute,
      container,
      limit: this.settings.facetLimitBeforeShowMore,
      showMore: true,
      showMoreLimit,
    });
    return refinementListWidget;
  }

  /**
   * Returns the generated selector for elements based on the current configuration.
   * @param {Object} configuration - Configuration object for rendering.
   * @returns {string} Generated selector.
   */
  getSelector(configuration) {
    const prefix = 'ais-refinement-list-';
    const suffix = configuration.isMobile ? '-mobile-container' : '-container';
    const selector = `${prefix}${configuration.container}${suffix}`;
    return selector;
  }

  /**
   * Renders the refinement list widget with the provided render options.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {boolean} isFirstRender - Indicates whether this is the first render.
   * @param {Object} renderOptions - Options for rendering the widget.
   * @returns {void}
   */
  render(configuration, isFirstRender, renderOptions) {
    const { canRefine, canToggleShowMore, items, refine, toggleShowMore, widgetParams } = renderOptions;
    const { container } = widgetParams;

    container.classList.toggle('hidden', !canRefine);
    container.innerHtml = '';

    if (isFirstRender) {
      const elements = this.renderWrapper(configuration);
      elements.forEach(element => container.appendChild(element));
    }

    const showMoreButton = container.querySelector('button');
    if (showMoreButton) {
      showMoreButton.classList.toggle('hidden', !canToggleShowMore);
      if (isFirstRender) showMoreButton.addEventListener('click', () => toggleShowMore());

      const showMoreButtonContent = showMoreButton.querySelector('span');
      if (showMoreButtonContent) showMoreButtonContent.textContent = renderOptions.isShowingMore ? this.settings.showLess : this.settings.showMore;
    }

    const unorderedList = container.querySelector('ul');

    if (unorderedList) {
      unorderedList.innerHTML = '';
      items.forEach(item => unorderedList.appendChild(this.renderContent(configuration, item)))
    }

    const inputList = container.querySelectorAll('input');
    if (inputList){ 
      inputList.innerHTML = '';
      inputList.forEach(input => input.addEventListener('click', event => refine(event.currentTarget.dataset.value)));
    }
  }

  /**
   * Renders the container for the refinement list widget.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Function} html - The HTML template function.
   * @returns {string} The rendered container.
   */
  renderContainer(configuration, html) {
    const selector = this.getSelector(configuration);
    const classNames = `${configuration.isMobile ? 'mobile-facets__details' : ''} facets__disclosure-vertical js-filter`;

    return html`<details class="${classNames} ${selector}" data-index="${selector}" id="details-${selector}" open="true"></details>`;
  }

  /**
   * Renders the content.
   * @param {Object} configuration - Configuration object for rendering.
   * @param {Object} item - The refinement item.
   * @returns {string} The rendered content HTML.
   */
  renderContent(configuration, item) {
    const id = `${configuration.container}-${item.label}`;
    const iconCheckbox = this.querySelector('algolia-icon-checkbox');
    const iconCheckboxContent = iconCheckbox ? iconCheckbox.render() : [];

    const listItem = document.createElement('li');
    listItem.classList.add('list-menu__item', 'facets__item');
    const label = document.createElement('label');
    label.classList.add('facet-checkbox', 'p-regular');
    label.setAttribute('for', id);
    const input = document.createElement('input');
    input.setAttribute('data-value', item.value);
    input.id = id;
    input.name = id;
    input.type = 'checkbox';
    input.checked = item.isRefined ? "checked" : "";
    
    const span = document.createElement('span');
    span.classList.add('facets__item__size-container', 'facet-size');
    span.textContent = item.label;


    label.appendChild(input);
    iconCheckboxContent.forEach(element => label.appendChild(element));
    label.appendChild(span);

    listItem.appendChild(label);

    return listItem;
  }

  /**
   * Renders the wrapper.
   * @param {Object} configuration - Configuration object for rendering.
   * @returns {string} The rendered wrapper HTML.
   */
  renderWrapper(configuration) {
    const id = `facet-${this.getSelector(configuration)}`;
    const title = configuration.title;
    const iconExpandCollapse = this.querySelector('algolia-icon-expand-collapse');
    const iconExpandCollapseContent = iconExpandCollapse ? iconExpandCollapse.render() : [];

    const summary = document.createElement('summary');
    summary.classList.add('facets__summary', 'caption-large', 'focus-offset');
    const summaryDiv = document.createElement('div');
    summaryDiv.classList.add('facets__summary-title');
    const summarySpan = document.createElement('span');
    summarySpan.classList.add('p-regular', 'p-bold');
    summarySpan.textContent = title;
    const iconDiv = document.createElement('div');
    iconDiv.classList.add('icon--expand-collapse');
    iconExpandCollapseContent.forEach(element => iconDiv.appendChild(element));

    summaryDiv.appendChild(summarySpan);
    summaryDiv.appendChild(iconDiv);

    summary.appendChild(summaryDiv);

    const div = document.createElement('div');
    div.id = id;
    const ul = document.createElement('ul');
    ul.classList.add('facets__list--vertical', 'list-unstyled', 'no-js-hidden');

    div.appendChild(ul);

    const button = document.createElement('button');
    button.classList.add('button-show-more', 'nobutton', 'link', 'underlined-link', 'no-js-hidden');
    button.type = 'button';
    const buttonSpan = document.createElement('span');
    buttonSpan.classList.add('label-show-more', 'p-link', 'p-regular', 'label-text');

    button.appendChild(buttonSpan);

    return [summary, div, button];
  }
}

customElements.define("algolia-refinement-list", AlgoliaRefinementList);